import React from 'react';
import keycloak from '../../utils/keycloak';
import { CompanyApi } from '../../services/company';

const portalName = 'HRMS Portal';

const features = [
  {
    icon: '🗂️',
    title: 'Centralized HR',
    desc: 'All your HR services in one secure portal.'
  },
  {
    icon: '🕒',
    title: 'Leave & Attendance',
    desc: 'Easy leave application and attendance tracking.'
  },
  {
    icon: '💸',
    title: 'Payroll',
    desc: 'Transparent, timely salary and payslip access.'
  },
  {
    icon: '🔒',
    title: 'Secure',
    desc: 'Government-grade security and privacy.'
  },
];

const LandingPage: React.FC = () => {
  const handleLogin = () => {
    keycloak.login();
  };

  // Fetch company details
  const { getPublicProfile } = CompanyApi();
  const [company, setCompany] = React.useState<any>(null);

  React.useEffect(() => {
    (async () => {
      const res = await getPublicProfile();
      if (res && res.data && res.data.data) {
        setCompany(res.data.data);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Typewriter effect for portal name
  const [displayed, setDisplayed] = React.useState('');
  React.useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setDisplayed(portalName.slice(0, i + 1));
      i++;
      if (i === portalName.length) clearInterval(interval);
    }, 90);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen font-sans flex flex-col bg-gradient-to-br from-[#d1fae5] via-[#b7e4c7] to-[#43b97f]">
      {/* Header Section */}
      <header className="w-full backdrop-blur-md bg-[#43b97f]/70 shadow-lg border-b border-[#43b97f]/30 min-h-[100px] flex flex-col sm:flex-row items-center justify-between px-4 py-4 sm:px-0 sm:py-0">
        {/* Logo on left (centered on mobile) */}
        <div className="flex items-center justify-center mb-2 sm:mb-0 sm:ml-10">
          <img
            src={company && company.companyLogo}
            alt="Solapur University Logo"
            className="h-[56px] w-auto object-contain border-2 border-white/70 shadow-md rounded-lg bg-white/80 p-2"
          />
        </div>
        {/* University and portal name in center (centered on mobile) */}
        <div className="flex-1 flex flex-col items-center justify-center mb-2 sm:mb-0">
          <div className="text-lg sm:text-2xl md:text-3xl font-extrabold text-white drop-shadow-lg mt-1 mb-1 tracking-wide text-center">
            {company && company.companyName}
          </div>
          <div className="text-base sm:text-lg md:text-xl font-medium text-white/90 drop-shadow-sm text-center">
            <span className="border-r-2 border-white pr-2">{displayed}</span>
          </div>
        </div>
        {/* Login button on right (centered on mobile) */}
        <div className="flex items-center justify-center sm:mr-10">
          <button
            onClick={handleLogin}
            className="bg-white/90 hover:bg-white text-[#16a34a] hover:text-[#15803d] border-none rounded-xl px-6 py-2 sm:px-8 sm:py-3 text-base sm:text-lg font-bold cursor-pointer shadow-lg transition-colors duration-200 outline-none backdrop-blur-md border border-white/70"
          >
            Login
          </button>
        </div>
      </header>

      {/* Features Section */}
      <main className="flex-1 flex flex-col items-center justify-center py-16 px-2">
        <section className="w-full max-w-6xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-extrabold text-[#10633a] mb-12 text-center drop-shadow-lg">Why use HRMS?</h3>
          <div className="grid gap-10 grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
            {features.map((f, idx) => (
              <div
                key={idx}
                className="backdrop-blur-lg bg-white/60 border border-[#43b97f]/20 rounded-2xl shadow-xl p-8 text-center flex flex-col items-center min-h-[180px] cursor-pointer transition-transform duration-200 will-change-transform hover:-translate-y-2 hover:scale-105 hover:shadow-2xl"
              >
                <span className="text-[44px] mb-4 drop-shadow-lg">{f.icon}</span>
                <div className="font-bold text-[#10633a] text-lg mb-2 drop-shadow">{f.title}</div>
                <div className="text-[#222] text-base drop-shadow-sm">{f.desc}</div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="backdrop-blur-md bg-[#e5efe7]/80 text-[#10633a] text-center text-base pt-8 pb-4 border-t border-[#43b97f]/20 shadow-lg mt-auto">
        <div className="mb-2">
          {company && company.companyAddress && (
            <div>{company.companyAddress}</div>
          )}
          {company && company.companyEmail && (
            <div>Email: {company.companyEmail}</div>
          )}
          {company && company.companyContactNumber && (
            <div>Phone: {company.companyContactNumber}</div>
          )}
        </div>
        <div className="text-[#9fbdab] text-sm mt-2">
          &copy; {new Date().getFullYear()} {company && company.companyName} HRMS Portal
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 